
import React, { useState, useEffect, useMemo } from "react";
import { 
  Search, Filter, Download, RefreshCw,
  ArrowLeft, ArrowRight, Star, Loader,
  Users, UserCheck, UserMinus, BarChart2, Calendar
} from "lucide-react";
import "./AnalyticsDashboardStyles.css";

import useCustomFetchQuery from '../../../Hooks/useCustomFecthQuery';

import DayFilter from "./DayFilter";
import EngagementFunnel from "./EngagementFunnel";
import AttendeeList from "./AttendeeList";
import DailyAttendanceChart from "./DailyAttendanceChart";
import PeakHoursChart from "./PeakHoursChart";
import TicketTypeDonutChart from "./TicketTypeDonutChart";
import RegistrationTrendChart from "./RegistrationTrendChart";
import { exportRegisteredAttendee } from "../../../utils/exportRegisteredAttendee";

const SafeComponent = ({ component: Component, componentName, ...props }) => {
  if (typeof Component !== 'function' && typeof Component !== 'string') return null;
  try {
    return <Component {...props} />;
  } catch (error) {
    console.warn(`⚠️ Error rendering ${componentName}:`, error);
    return null;
  }
};

const SessionPerformanceTable = ({ sessions = [], onSessionClick }) => {
  if (!sessions || sessions.length === 0) {
    return (
      <div className="session-performance-container">
        <h2 className="session-performance-title">Session Performance</h2>
        <div className="empty-state">
          <BarChart2 size={48} className="empty-icon" />
          <p>No session data available</p>
          <p className="empty-subtext">Sessions will appear here once added to your event</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-performance-container">
      <div className="session-performance-header">
        <h2 className="session-performance-title">Session Performance</h2>
        <span className="session-count-badge">{sessions.length} sessions</span>
      </div>
      <div className="session-performance-table-container">
        <table className="session-performance-table">
          <thead>
            <tr>
              <th>Session</th><th>Speaker</th><th>Day</th><th>Time</th>
              <th>Capacity</th><th>Attended</th><th>Fill Rate</th><th>Rating</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => (
              <tr key={session.id} className="session-row" onClick={() => onSessionClick?.(session.id)} style={{ cursor: onSessionClick ? 'pointer' : 'default' }}>
                <td data-label="Session" className="session-title-cell">{session.title}</td>
                <td data-label="Speaker">{session.speaker}</td>
                <td data-label="Day"><span className="day-badge">{session.day}</span></td>
                <td data-label="Time">{session.time}</td>
                <td data-label="Capacity">{session.capacity}</td>
                <td data-label="Attended">{session.attended}</td>
                <td data-label="Fill Rate">
                  <div className="fill-rate-cell">
                    <div className="funnel-bar" style={{ height: '12px', width: '80px' }}>
                      <div className="funnel-bar-fill" style={{ width: `${Math.min(session.fillRate, 100)}%`, backgroundColor: session.fillRate >= 90 ? '#10b981' : session.fillRate >= 70 ? '#f97316' : '#ef4444' }} />
                    </div>
                    <span className="fill-rate-value">{session.fillRate}%</span>
                  </div>
                </td>
                <td data-label="Rating">{session.rating > 0 ? <><Star size={18} fill="#fbbf24" color="#fbbf24" /> {session.rating.toFixed(1)}</> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const transformAnalyticsData = (analyticsResponse, attendeeListResponse, eventDetails, sessionsResponse, reportsResponse) => {
  // console.group('🔍 Transform Analytics Data');
  // console.log('AttendeeListResponse:', attendeeListResponse);
  // console.log('ReportsResponse:', reportsResponse);
  
  let attendees = [];
  
  if (Array.isArray(attendeeListResponse)) {
    attendees = attendeeListResponse;
    // console.log('✅ Format 1: Direct array');
  } else if (attendeeListResponse?.data?.invitedAttendees && Array.isArray(attendeeListResponse.data.invitedAttendees)) {
    attendees = attendeeListResponse.data.invitedAttendees;
    // console.log('✅ Format 2: data.invitedAttendees array');
  } else if (attendeeListResponse?.invitedAttendees && Array.isArray(attendeeListResponse.invitedAttendees)) {
    attendees = attendeeListResponse.invitedAttendees;
    // console.log('✅ Format 3: invitedAttendees array');
  } else if (attendeeListResponse?.data && Array.isArray(attendeeListResponse.data)) {
    attendees = attendeeListResponse.data;
    // console.log('✅ Format 4: data array');
  } else if (attendeeListResponse?.attendees && Array.isArray(attendeeListResponse.attendees)) {
    attendees = attendeeListResponse.attendees;
    // console.log('✅ Format 5: attendees array');
  } else if (attendeeListResponse?.result && Array.isArray(attendeeListResponse.result)) {
    attendees = attendeeListResponse.result;
    // console.log('✅ Format 6: result array');
  } else {
    // console.warn('⚠️ Could not extract attendees from response');
  }
  
  // console.log(`📊 Total attendees extracted: ${attendees.length}`);
  // if (attendees.length > 0) {
  //   console.log('Sample attendee:', attendees[0]);
  // }
  // console.groupEnd();

  const reportsData = reportsResponse?.data || reportsResponse || {};
  const attendanceFromReports = reportsData.attendance || reportsData.attendedCount || reportsData.checkedIn || 0;
  
  // console.log('📈 Reports API attendance:', attendanceFromReports);
  
  const totalRegistrations = reportsData.invitedAttendees || attendees.length || 0;
  const checkedInCount = attendanceFromReports > 0 ? attendanceFromReports : 0;

  // ✅ Extract ALL custom field keys that actually appear on the form / responses
  //    and normalize them so we don't show duplicate columns (e.g. "Gender" vs "gender").
  //    Also skip any keys that duplicate core columns like firstName/lastName/email.
  const coreFieldNames = [
    "name",
    "first name",
    "firstname",
    "last name",
    "lastname",
    "email",
    "phone",
    "phone number",
    "phonenumber",
    "company",
    "organisation",
    "organization",
    "ticket",
    "ticket type",
    "day",
    "status",
    "sessions",
  ];

  const customFieldMap = new Map(); // normalizedKey -> displayLabel
  attendees.forEach(att => {
    if (att.customFields) {
      if (Array.isArray(att.customFields)) {
        att.customFields.forEach(cf => {
          const rawKey = cf.label || cf.fieldKey;
          const key = rawKey && rawKey.toString().trim();
          const normalized = key && key.toLowerCase();
          if (normalized && !coreFieldNames.includes(normalized) && !customFieldMap.has(normalized)) {
            customFieldMap.set(normalized, key);
          }
        });
      } else if (typeof att.customFields === 'object') {
        Object.keys(att.customFields).forEach(rawKey => {
          const key = rawKey && rawKey.toString().trim();
          const normalized = key && key.toLowerCase();
          if (normalized && !coreFieldNames.includes(normalized) && !customFieldMap.has(normalized)) {
            customFieldMap.set(normalized, key);
          }
        });
      }
    }
  });

  // ✅ Use all discovered custom fields (sorted alphabetically for stable UI)
  const customFieldsList = Array.from(customFieldMap.values()).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
  
  //console.log('🏷️ Custom fields (from form data):', customFieldsList);

  const kpiData = {
    totalRegistrations,
    checkedIn: checkedInCount,
    noShows: Math.max(0, totalRegistrations - checkedInCount),
    attendanceRate: totalRegistrations > 0 ? parseFloat((Math.min(checkedInCount, totalRegistrations) / totalRegistrations * 100).toFixed(1)) : 0,
    vipAttendees: attendees.filter(a => {
      const t = (a.ticketType || a.type || a.ticket_type || a.package || '').toLowerCase();
      return t === 'vip' || t === 'vip ticket';
    }).length,
    avgSessionsPerPerson: totalRegistrations > 0 ? parseFloat((attendees.reduce((s, a) => s + (a.sessionsAttended || a.sessions_attended || 0), 0) / totalRegistrations).toFixed(1)) : 0
  };

  const eventDays = eventDetails?.eventDays || eventDetails?.days || ['Day 1'];
 
  const printedBadges = eventDetails?.badgesPrinted || eventDetails?.data?.badgesPrinted || [];
  const checkedInEmails = new Set(
    (printedBadges || []).map(b => (b.email || "").toLowerCase()).filter(Boolean)
  );
  const checkedInIds = new Set(
    (printedBadges || []).map(b => b._id || b.id).filter(Boolean)
  );

  const attendeeData = {
    attendees: attendees.map((att, idx) => {
      const emailLower = (att.email || "").toLowerCase();
      const idValue = att._id || att.id;
      const inBadgesPrinted =
        (emailLower && checkedInEmails.has(emailLower)) ||
        (idValue && checkedInIds.has(idValue));

      const hasLocalCheckIn =
        inBadgesPrinted ||
        att.checkedIn === true ||
        att.isCheckedIn === true ||
        att.badgePrintedAt ||
        att.checkInTime;
      
      //  Extract custom field values for each attendee (whatever was filled on the form),
      //    mapped to the normalized display label so columns align and don't duplicate.
      const customFieldValues = {};
      if (att.customFields) {
        if (Array.isArray(att.customFields)) {
          att.customFields.forEach(cf => {
            const rawKey = cf.label || cf.fieldKey;
            const key = rawKey && rawKey.toString().trim();
            const normalized = key && key.toLowerCase();
            const displayKey = (normalized && customFieldMap.get(normalized)) || key;
            if (displayKey) {
              customFieldValues[displayKey] = cf.value || cf.answer || '';
            }
          });
        } else if (typeof att.customFields === 'object') {
          Object.entries(att.customFields).forEach(([rawKey, value]) => {
            const key = rawKey && rawKey.toString().trim();
            const normalized = key && key.toLowerCase();
            const displayKey = (normalized && customFieldMap.get(normalized)) || key;
            if (displayKey) {
              customFieldValues[displayKey] = value;
            }
          });
        }
      }
      
      return {
        id: att._id || att.id || `temp-${idx}`,
        name: att.name || `${att.firstName || ''} ${att.lastName || ''}`.trim() || att.email?.split('@')[0] || 'Anonymous',
        firstName: att.firstName || '',
        lastName: att.lastName || '',
        email: att.email || '',
        phoneNumber: att.phoneNumber || att.phone || '',
        company: att.organization || att.company || att.organisation || 'N/A',
        ticketType: (att.type || att.ticketType || att.ticket_type || att.package || 'General').toLowerCase(),
        day: att.day || att.eventDay || eventDays[0] || 'Day 1',
        checkedIn: hasLocalCheckIn,
        checkInTime: att.checkInTime || att.checkedInAt || att.badgePrintedAt || null,
        badgePrinted: !!(att.badgePrintedAt || att.badge_printed_at),
        noShow: !hasLocalCheckIn,
        sessionsAttended: att.sessionsAttended || att.sessions_attended || 0,
        createdAt: att.createdAt || att.registeredAt || att.created_at || new Date().toISOString(),
        status: hasLocalCheckIn ? 'checked-in' : 'registered',
        customFields: customFieldValues,
        rawCustomFields: att.customFields
      };
    })
  };

  const dailyAttendance = eventDays.map(day => {
    const dayAttendees = attendeeData.attendees.filter(a => a.day === day);
    const expected = attendanceFromReports > 0 && totalRegistrations > 0 ? Math.round((dayAttendees.length / totalRegistrations) * attendanceFromReports) : dayAttendees.filter(a => a.checkedIn).length;
    return { day, registered: dayAttendees.length, attended: expected };
  }).filter(d => d.registered > 0 || d.attended > 0);

  if (dailyAttendance.length === 0 && attendeeData.attendees.length > 0) {
    dailyAttendance.push({ day: eventDays[0] || 'Day 1', registered: attendeeData.attendees.length, attended: attendanceFromReports });
  }

  const ticketMap = {};
  attendeeData.attendees.forEach(a => { const t = a.ticketType || 'general'; ticketMap[t] = (ticketMap[t] || 0) + 1; });
  const ticketTypes = Object.entries(ticketMap).map(([type, count]) => ({ type, count }));

  const trendMap = {};
  attendeeData.attendees.forEach(a => { try { const d = new Date(a.createdAt).toISOString().split('T')[0]; trendMap[d] = (trendMap[d] || 0) + 1; } catch {} });
  const registrationTrend = Object.entries(trendMap).map(([date, count]) => ({ date, count })).sort((a, b) => new Date(a.date) - new Date(b.date));

  const active = attendeeData.attendees.filter(a => (a.sessionsAttended || 0) >= 2).length;
  const engaged = attendeeData.attendees.filter(a => (a.sessionsAttended || 0) >= 3).length;
  const funnelData = { registered: totalRegistrations, checkedIn: checkedInCount, active, highlyEngaged: engaged };

  const normalizeSession = (s, i) => {
    const attended = s.attendees?.length || s.attendedCount || s.attended || 0;
    const cap = s.capacity || s.maxAttendees || 100;
    const fill = cap > 0 ? Math.round((attended / cap) * 100) : 0;
    let time = 'TBA';
    if (s.startTime) { try { time = new Date(s.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }); } catch { time = s.timeSlot || s.time || 'TBA'; } }
    else { time = s.timeSlot || s.time || 'TBA'; }
    return { id: s._id || s.id || `session-${i}`, title: s.title || s.name || 'Untitled', speaker: s.speaker?.name || s.speakerName || s.speaker || 'TBA', day: s.day || s.eventDay || eventDays[0] || 'Day 1', time, capacity: cap, attended, fillRate: fill, rating: s.rating || s.averageRating || (s.feedback?.average || 0), _raw: s };
  };

  let sessions = [];
  if (Array.isArray(sessionsResponse)) sessions = sessionsResponse.map(normalizeSession);
  else if (sessionsResponse?.data && Array.isArray(sessionsResponse.data)) sessions = sessionsResponse.data.map(normalizeSession);
  else if (sessionsResponse?.sessions && Array.isArray(sessionsResponse.sessions)) sessions = sessionsResponse.sessions.map(normalizeSession);
  sessions.sort((a, b) => (a.day || '').localeCompare(b.day || '') || (a.time || '').localeCompare(b.time || ''));

  return { 
    kpiData, 
    chartData: { dailyAttendance, peakHours: analyticsResponse?.data?.peakHours || analyticsResponse?.peakHours || [], ticketTypes, registrationTrend }, 
    funnelData, 
    sessionData: { sessions }, 
    attendeeData,
    customFieldsList
  };
};

const AnalyticsDashboard = ({ eventDetails, token, userID }) => {
  const [selectedDay, setSelectedDay] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ticketTypeFilter, setTicketTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [exporting, setExporting] = useState(false);

  const eventID = eventDetails?._id;

  // Debug log commented out
  // useEffect(() => { console.log('🎯 Dashboard Event ID:', eventID); }, [eventID]);

  const { 
    data: analyticsResponse, 
    error: analyticsError, 
    isLoading: isLoadingAnalytics, 
    refetch: refetchAnalytics, 
    isRefetching: isRefetchingAnalytics 
  } = useCustomFetchQuery(
    ["eventAnalytics", eventID], 
    `${process.env.REACT_APP_API}analytics/${eventID}`, 
    token, 
    { enabled: !!eventID, staleTime: 5*60*1000, retry: 2 }
  );

  const { 
    data: attendeeListResponse, 
    error: attendeeListError, 
    isLoading: isLoadingAttendeeList, 
    refetch: refetchAttendees, 
    isRefetching: isRefetchingAttendees 
  } = useCustomFetchQuery(
    ["eventAttendeesList", eventID], 
    `${process.env.REACT_APP_API}attendees/${eventID}/invited`, 
    token, 
    { enabled: !!eventID, staleTime: 30*1000, retry: 2, onError: e => console.error('❌ Attendees:', e?.message) }
  );

  const { 
    data: reportsResponse, 
    error: reportsError, 
    isLoading: isLoadingReports, 
    refetch: refetchReports, 
    isRefetching: isRefetchingReports 
  } = useCustomFetchQuery(
    ["eventReports", eventID], 
    `${process.env.REACT_APP_API}reports/${eventID}`, 
    token, 
    { enabled: !!eventID, staleTime: 30*1000, retry: 2, onError: e => console.warn('⚠️ Reports:', e?.message) }
  );

  const { 
    data: sessionsResponse, 
    error: sessionsError, 
    isLoading: isLoadingSessions, 
    refetch: refetchSessions, 
    isRefetching: isRefetchingSessions 
  } = useCustomFetchQuery(
    ["eventSessions", eventID], 
    `${process.env.REACT_APP_API}sessions/event/${eventID}`, 
    token, 
    { enabled: !!eventID, staleTime: 5*60*1000, retry: 2 }
  );

  const isLoadingData = isLoadingAnalytics || isLoadingAttendeeList || isLoadingReports || isLoadingSessions;
  const isRefetching = isRefetchingAnalytics || isRefetchingAttendees || isRefetchingReports || isRefetchingSessions;
  const dataError = analyticsError || attendeeListError || sessionsError;

  const handleRefresh = async () => { 
    // console.log('🔄 Manual refresh'); 
    await Promise.all([refetchAnalytics(), refetchAttendees(), refetchReports(), refetchSessions()]); 
  };

  useEffect(() => {
    // console.log('🔄 Auto-refresh setup');
    const onVisible = () => { if (document.visibilityState === 'visible') refetchReports(); };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(() => { /* console.log('⏰ Polling check-ins'); */ refetchReports(); }, 15000);
    return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(interval); };
  }, [refetchReports]);

  const analyticsData = transformAnalyticsData(
    analyticsResponse, 
    attendeeListResponse, 
    eventDetails, 
    sessionsResponse, 
    reportsResponse
  );
  
  const { 
    kpiData = {}, 
    chartData = {}, 
    funnelData = {}, 
    sessionData = { sessions: [] }, 
    attendeeData = { attendees: [] },
    customFieldsList = []
  } = analyticsData;

  const filteredAttendees = (attendeeData.attendees || []).filter(a => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      a.name?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.company?.toLowerCase().includes(q) ||
      a.phoneNumber?.toLowerCase().includes(q);

    // Status filter semantics:
    // - "all": everyone
    // - "registered": everyone who registered (no extra filtering; includes checked-in + not yet checked-in + any potential no-shows)
    // - "checked-in": only checked-in
    // - "no-show": only no-shows
    const matchStatus =
      statusFilter === "all" ||
      statusFilter === "registered" ||
      (statusFilter === "checked-in" && a.checkedIn) ||
      (statusFilter === "no-show" && a.noShow);

    const matchTicket =
      ticketTypeFilter === "all" ||
      a.ticketType?.toLowerCase() === ticketTypeFilter;
    const matchDay = selectedDay === "all" || a.day === selectedDay;

    return matchSearch && matchStatus && matchTicket && matchDay;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = filteredAttendees.slice(indexOfLast - itemsPerPage, indexOfLast);
  const totalPages = Math.ceil(filteredAttendees.length / itemsPerPage);

  const exportToCSV = async () => {
    setExporting(true);
    try {
      // Adapt filtered attendees into the shape expected by exportRegisteredAttendee
      const dataForExport = filteredAttendees.map(a => ({
        firstName: a.firstName || "",
        lastName: a.lastName || "",
        email: a.email || "",
        phoneNumber: a.phoneNumber || "",
        // Convert customFields object into array of { label, value }
        customFields: Object.entries(a.customFields || {}).map(([label, value]) => ({
          label,
          value,
        })),
      }));

      exportRegisteredAttendee(dataForExport);
    } catch (err) { 
      console.error('Export error:', err); 
    } finally { 
      setExporting(false); 
    }
  };

  if (!eventID) return <div className="analytics-container analytics-error"><h3>Event Data Missing</h3><p>Analytics require a valid event ID.</p></div>;
  
  if (isLoadingData && !analyticsResponse && !attendeeListResponse && !sessionsResponse) {
    return (
      <div className="analytics-container">
        <div className="skeleton-loader">
          <div className="skeleton-header"/>
          <div className="skeleton-kpi-grid">{[...Array(6)].map((_,i)=><div key={i} className="skeleton-card"/>)}</div>
        </div>
      </div>
    );
  }
  
  if (dataError) {
    return (
      <div className="analytics-container analytics-error">
        <div className="error-container">
          <h3>Failed to Load Analytics</h3>
          <p>{dataError.message||"Please try again later."}</p>
          <button className="retry-btn" onClick={handleRefresh} disabled={isRefetching}>
            {isRefetching?'Refreshing...':'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div style={{fontSize:'12px',color:'#666',marginBottom:'10px',textAlign:'right'}}>
        🟢 Attendees: {attendeeData.attendees?.length||0} | ✅ Checked In: {kpiData.checkedIn??0} | 📊 Sessions: {sessionData.sessions?.length||0} {customFieldsList.length > 0 && `| 🏷️ Custom Fields: ${customFieldsList.length}`}
      </div>

      <div className="analytics-header">
        <div className="analytics-header-content">
          <div className="analytics-title-section">
            <h1 className="analytics-title">{eventDetails?.eventName||"Event Analytics"}</h1>
            <p className="analytics-subtitle">Real-time insights for your event performance</p>
          </div>
          <div className="analytics-actions">
            <button className="analytics-refresh-btn" onClick={handleRefresh} disabled={isRefetching}>
              <RefreshCw size={18} className={isRefetching?'spin':''}/>
              <span>{isRefetching?'Refreshing...':'Refresh'}</span>
            </button>
            <button className="analytics-export-btn" onClick={exportToCSV} disabled={exporting||filteredAttendees.length===0}>
              {exporting?<><Loader size={18}/><span>Exporting...</span></>:<><Download size={18}/><span>Export CSV</span></>}
            </button>
          </div>
        </div>
      </div>

      <div className="day-filter-wrapper">
        <DayFilter selectedDay={selectedDay} onDayChange={setSelectedDay} days={eventDetails?.eventDays||["all","Day 1","Day 2","Day 3"]}/>
      </div>

      <div className="kpi-linear">
        {[
          {title:'Total Registrations',value:kpiData.totalRegistrations??0,icon:Users,color:'#f97316'},
          {title:'Checked In',value:kpiData.checkedIn??0,icon:UserCheck,color:'#10b981'},
          {title:'No Shows',value:kpiData.noShows??0,icon:UserMinus,color:'#f59e0b'},
          {title:'Attendance Rate',value:kpiData.attendanceRate!=null?`${kpiData.attendanceRate}%`:'0%',icon:BarChart2,color:'#3b82f6'},
          {title:'VIP Attendees',value:kpiData.vipAttendees??0,icon:Users,color:'#f59e0b'},
          {title:'Avg Sessions/Person',value:kpiData.avgSessionsPerPerson!=null?kpiData.avgSessionsPerPerson:'0.0',icon:Calendar,color:'#3b82f6'}
        ].map((kpi,i)=>(
          <div className="kpi-card" key={i}>
            <div className="kpi-card-header">
              <h3 className="kpi-card-title">{kpi.title}</h3>
              <div className="kpi-icon-container">
                <div className="kpi-icon">
                  <kpi.icon size={20} color={kpi.color}/>
                </div>
              </div>
            </div>
            <div className="kpi-card-value">{kpi.value}</div>
          </div>
        ))}
      </div>

      <div className="charts-linear">
        <div className="chart-wrapper-container">
          {chartData.dailyAttendance?.length>0?
            <DailyAttendanceChart data={chartData.dailyAttendance}/>:
            <div className="chart-empty-state">No attendance data</div>
          }
        </div>
        <div className="chart-wrapper-container">
          {chartData.peakHours?.length>0?
            <PeakHoursChart data={chartData.peakHours}/>:
            <div className="chart-empty-state">No peak hours data</div>
          }
        </div>
        <div className="chart-wrapper-container">
          {chartData.ticketTypes?.length>0?
            <TicketTypeDonutChart data={chartData.ticketTypes}/>:
            <div className="chart-empty-state">No ticket data</div>
          }
        </div>
        <div className="chart-wrapper-container">
          {chartData.registrationTrend?.length>0?
            <RegistrationTrendChart data={chartData.registrationTrend}/>:
            <div className="chart-empty-state">No trend data</div>
          }
        </div>
      </div>

      <div className="funnel-linear">
        {funnelData.registered>0?
          <SafeComponent component={EngagementFunnel} componentName="EngagementFunnel" funnelData={funnelData} loading={isLoadingData}/>:
          <div className="funnel-empty-state">
            <p>No funnel data available</p>
            <p style={{fontSize:'12px',color:'#999'}}>Total: {kpiData.totalRegistrations||0}</p>
          </div>
        }
      </div>

      <div className="session-linear">
        <SessionPerformanceTable
          sessions={sessionData.sessions}
          onSessionClick={async (id) => {
            // console.log('Session:', id);
          }}
        />
      </div>

      {/* ✅ ATTENDEE LIST WITH WHITELISTED CUSTOM FIELDS */}
      <div className="attendee-list-section">
        <div className="attendee-list-header">
          <h2 className="attendee-list-heading">
            Attendee List <span className="attendee-list-count">({filteredAttendees.length})</span>
          </h2>
          <button className="export-csv-btn" onClick={exportToCSV} disabled={exporting||filteredAttendees.length===0}>
            <Download size={16}/><span>Export CSV</span>
          </button>
        </div>

        {attendeeData.attendees?.length===0 ? (
          <div className="empty-state">
            <p>No attendees found</p>
          </div>
        ) : (
          <>
            <div className="attendee-list-filters">
              <div className="search-box">
                <Search size={16} className="search-icon"/>
                <input type="text" placeholder="Search attendees..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} className="search-input"/>
              </div>
              <div className="filter-container">
                <div className="filter-group">
                  <Filter size={16} className="filter-icon"/>
                  <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="filter-select">
                    <option value="all">All Statuses</option>
                    <option value="registered">Registered</option>
                    <option value="checked-in">Checked In</option>
                    <option value="no-show">No Show</option>
                  </select>
                </div>
                <div className="filter-group">
                  <Filter size={16} className="filter-icon"/>
                  <select value={ticketTypeFilter} onChange={e=>setTicketTypeFilter(e.target.value)} className="filter-select">
                    <option value="all">All Ticket Types</option>
                    <option value="general">General</option>
                    <option value="vip">VIP</option>
                    <option value="speaker">Speaker</option>
                    <option value="sponsor">Sponsor</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="attendee-list-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Company</th>
                    <th>Ticket</th>
                    <th>Day</th>
                    <th>Status</th>
                    <th>Sessions</th>
                    {/* ✅ Whitelisted Custom Field Columns Only */}
                    {customFieldsList.map(field => (
                      <th key={field} className="custom-field-header">{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8 + customFieldsList.length}
                        className="empty-state no-results-row"
                      >
                        No results match filters
                      </td>
                    </tr>
                  ) : (
                    currentItems.map(a=>(
                      <tr key={a.id}>
                        <td>{a.name}</td>
                        <td>{a.email}</td>
                        <td>{a.phoneNumber}</td>
                        <td>{a.company}</td>
                        <td><span className={`ticket-badge ${a.ticketType?.toLowerCase()||'general'}`}>{a.ticketType}</span></td>
                        <td>{a.day}</td>
                        <td><span className={`status-badge ${a.checkedIn?'checked-in':a.noShow?'no-show':'registered'}`}>
                          {a.checkedIn?'Checked In':a.noShow?'No Show':'Registered'}
                        </span></td>
                        <td>{a.sessionsAttended}</td>
                        {/* ✅ Whitelisted Custom Field Values Only */}
                        {customFieldsList.map(field => (
                          <td key={field} className="custom-field-cell">{a.customFields?.[field] || '—'}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages>1 && currentItems.length>0 &&
              <div className="pagination-controls">
                <button className="pagination-btn" disabled={currentPage===1} onClick={()=>setCurrentPage(p=>p-1)}>
                  <ArrowLeft size={18}/><span>Prev</span>
                </button>
                {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                  let p=totalPages<=5?i+1:currentPage<=3?i+1:currentPage>=totalPages-2?totalPages-4+i:currentPage-2+i;
                  return<button key={p} className={`pagination-btn ${currentPage===p?'active':''}`} onClick={()=>setCurrentPage(p)}>{p}</button>;
                })}
                <button className="pagination-btn" disabled={currentPage===totalPages} onClick={()=>setCurrentPage(p=>p+1)}>
                  <ArrowRight size={18}/><span>Next</span>
                </button>
              </div>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;